// This file contains unfortunate data transformations
// which require the use of 'any' type to work around.
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    ContentFile,
    ContentItem,
    ContentUrl,
    ExperienceListingResponse,
    LogoSetItem,
    PublisherLogoItem,
    SCHEMA_VERSION,
} from '@oneappexchange/oaf-schema';
import { produce } from 'immer';
import { LISTING_SERVICE_API_VERSION, LISTING_SERVICE_URL, USE_STATIC_DATA } from '~/modules/config';
import { generateMediaUrl } from '~/modules/utilities/asset_utils';
import { CORRELATION_ID_PARAM, randomUUID } from '~/modules/utilities/cross_env_utils';
import { handleException } from '~/modules/utilities/error_utilities';
import { HttpRequestConfig, http } from '~/modules/utilities/http';
import { randomIntBetween } from '~/modules/utilities/number_utils';

const iconMap = {
    File: 'notebook',
    Url: 'new_window',
};

/**
 * Type containing the results of transformations done to the ExperienceListingResponse by the ListingService in appx-site package.
 */
export interface ListingResponse extends ExperienceListingResponse {
    tzId?: string;
    oafId?: string;
    appExchangeId?: string;
    external_id?: string;
    type: string;
    //TODO Define the real location of the more from this provider data
    moreFromThisProvider?: {
        id: string;
        listingUrl: string;
        averageRating: string;
        reviewsAmount: string;
        publisher: string;
        listingCategories: [];
        imageURL: string;
        title: string;
        description: string;
    };
}

export interface Resource {
    id: string;
    icon: string;
    caption: string;
    url: string;
    shouldDownload: boolean;
    fileName?: string;
}

export interface ResourceSection {
    type: string;
    resources: Resource[];
    column: number;
    barColor: string;
}

const resourceColumns: Record<number, number> = {
    0: 1,
    1: 2,
    2: 3,
    3: 1,
    4: 3,
    5: 2,
    6: 1,
    7: 3,
    8: 2,
};

/** Adds column and a unique random bar color to each section */
const addDynamicData = (sections: ResourceSection[]) => {
    const colors = ['#FF5D2D', '#DD7A01', '#3BA755', '#1B96FF', '#7F8CED', '#CA8501', '#06A59A', '#0D9DDA'];
    return sections.map((section, index) => {
        const colorPos = randomIntBetween(0, colors.length);
        return {
            ...section,
            column: resourceColumns[index],
            barColor: colors.splice(colorPos, 1)[0],
        };
    });
};

const buildResourceSection: (item: ContentItem, resourcePosition: number) => ResourceSection = (
    item: ContentItem,
    resourcePosition: number,
) => {
    const resourceSection: ResourceSection = {
        type: item.data.type,
        resources: [],
        column: resourceColumns[resourcePosition],
        barColor: '',
    };

    return resourceSection;
};

export const buildResource = (item: ContentItem) => {
    const resource: Resource = {
        id: item.external_id,
        icon: iconMap[item.type],
        caption: item.data.title,
        url: item.type === 'Url' ? (item.data as ContentUrl).url : generateMediaUrl((item.data as ContentFile).mediaId),
        shouldDownload: item.type === 'File',
        fileName: item.type === 'File' ? (item.data as ContentFile).fileName || item.data.title : undefined,
    };

    return resource;
};

// transforms are applied as plugin and extension arrays are processed;
// create extra transforms as needed using type as key
const transforms: any = {
    'listing/plugins/LogoSet': (element: any) => {
        const value: any = {};
        (element.data.items as LogoSetItem[])?.forEach((logo) => {
            value[logo.logoType] = logo.mediaId;
        });
        return value;
    },
    'publisher/plugins/PublisherLogo': (element: any) => {
        const value: any = {};
        (element.data.items as PublisherLogoItem[])?.forEach((logo) => {
            value[logo.logoType] = logo.mediaId;
        });
        return value;
    },
    'listing/plugins/Content': (element: any) => {
        const sections: any = {};
        (element.data.items as ContentItem[])?.forEach((item, index) => {
            if (!item.data.title) {
                return;
            }

            const section: ResourceSection = (sections[item.data.type] =
                sections[item.data.type] || buildResourceSection(item, index));

            section.resources.push(buildResource(item));
        });

        return addDynamicData(Object.values(sections));
    },
};

const config: HttpRequestConfig = {
    transformResponse: [
        (jsonData) => {
            return jsonData ? JSON.parse(jsonData) : undefined;
        },
        (data) => {
            return data ? getListingResponse(data) : undefined;
        },
    ],
};

/**
 *  !!! WARNING !!! DO NOT EXTEND THIS FUNCTION
 *
 * THIS WAS A BAD PATTERN THAT IS NOW DIFFICULT TO UNDO
 */
export function getListingResponse(listingResponse: ExperienceListingResponse) {
    //ListingResponse contains all listing related data including publisher.
    //In the future it will be expanded to include review summary data and more.
    return produce(listingResponse, (draft: any) => {
        // new field used at Analtytics Instrumentation to explicitly indicate oafId listingId value
        draft.oafId = listingResponse.external_id;

        // change extensions and plugins from array to dictionary to make
        // easier to use; some elements may need transform in addition
        draft.plugins?.forEach((element: any) => {
            let elementData: any = element.data;
            if (transforms[element.pluginType]) {
                elementData = transforms[element.pluginType](element);
            }
            draft[element.pluginType] = elementData;
        });

        // TODO: I think we're no longer directly accessing extensions out of the listing response object, this code can be deleted?
        draft.extensions?.forEach((element: any) => {
            let elementData: any = element.data;
            if (transforms[element.extensionType]) {
                elementData = transforms[element.extensionType](element);
            }
            draft[element.extensionType] = elementData;
        });

        const publisher: any = draft.publisher;
        publisher?.plugins?.forEach((element: any) => {
            let elementData: any = element.data;
            if (transforms[element.pluginType]) {
                elementData = transforms[element.pluginType](element);
            }
            publisher[element.pluginType] = elementData;
        });

        // map the extensions to make data easier to access
        publisher?.extensions?.forEach((element: any) => {
            publisher[element.extensionType] = element.data;
        });
    }) as unknown as ListingResponse;
}

export class ListingService {
    getListing(
        id: string,
        oafCookie?: string,
        correlationId?: string,
    ): Promise<{ listing: ListingResponse; headers: AnyObject }> {
        let listingPromise: Promise<{ data: ListingResponse; headers: AnyObject }>;
        if (USE_STATIC_DATA) {
            listingPromise = import(`~/modules/services/__data__/a0N300000055ailEAA.json`).then((result) => ({
                data: getListingResponse(result as unknown as ExperienceListingResponse),
                headers: {},
            }));
        } else {
            const headers: {
                Version: string;
                SchemaVersion: string;
                [CORRELATION_ID_PARAM]: string;
                oaf_canary?: string;
            } = {
                Version: LISTING_SERVICE_API_VERSION || '1',
                SchemaVersion: SCHEMA_VERSION,
                [CORRELATION_ID_PARAM]: correlationId ?? randomUUID(),
            };

            if (oafCookie) {
                headers.oaf_canary = oafCookie;
            }

            listingPromise = http.get(`${LISTING_SERVICE_URL}/${id}`, {
                ...config,
                headers,
            });
        }

        return listingPromise
            .then(({ data, headers }) => {
                return { listing: data, headers: headers };
            })
            .catch((error) => {
                handleException(error, 'listing_service.getListing', {
                    listingId: id,
                    correlationId,
                    oafCookie: oafCookie || undefined,
                    response: error?.response?.data,
                });
                throw error;
            });
    }
}
